import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "device-management" }
const _hoisted_2 = { class: "search-section" }
const _hoisted_3 = { class: "button-group" }
const _hoisted_4 = { class: "main-content" }
const _hoisted_5 = { class: "left-content" }
const _hoisted_6 = { class: "card-header" }
const _hoisted_7 = { class: "header-actions" }
const _hoisted_8 = ["src"]
const _hoisted_9 = ["src"]
const _hoisted_10 = {
  class: "action-buttons",
  style: {"display":"flex","justify-content":"center","align-items":"center","width":"100%"}
}
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { class: "table-footer" }
const _hoisted_13 = { class: "right-content" }
const _hoisted_14 = { class: "stats-grid" }
const _hoisted_15 = { class: "stat-item online" }
const _hoisted_16 = { class: "stat-icon" }
const _hoisted_17 = { class: "stat-content" }
const _hoisted_18 = { class: "stat-number" }
const _hoisted_19 = { class: "stat-item offline" }
const _hoisted_20 = { class: "stat-icon" }
const _hoisted_21 = { class: "stat-content" }
const _hoisted_22 = { class: "stat-number" }
const _hoisted_23 = { class: "stat-item error" }
const _hoisted_24 = { class: "stat-icon" }
const _hoisted_25 = { class: "stat-content" }
const _hoisted_26 = { class: "stat-number" }
const _hoisted_27 = { class: "stat-item total" }
const _hoisted_28 = { class: "stat-icon" }
const _hoisted_29 = { class: "stat-content" }
const _hoisted_30 = { class: "stat-number" }
const _hoisted_31 = { class: "dialog-footer" }

import { ref, reactive, onMounted, onUnmounted, nextTick, computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox, FormInstance, FormRules } from "element-plus";
import {
  Search,
  Refresh,
  Plus,
  Monitor,
  Warning,
  DataBoard,
  Phone,
} from "@element-plus/icons-vue";
import wifiIcon from "@/assets/images/WIFI.svg";
import cellularIcon from "@/assets/images/Cellular 4G-1.svg";
import { useStore } from "vuex";
import apiClient from "../services/apiClient";
import config from "@/config";
import { logger } from "../utils/logger";
import { DataService } from "../services/dataService";
import { formatDateTime } from "../utils/dateTimeUtils";
import jsCookie from "js-cookie";
import {
  useCallDialog,
  getDeviceDisplayName,
} from "@/composables/useCallDialog";

// 设备类型定义
interface Device {
  device_id: string;
  device_name: string;
  status?: string;
  license_status?: string;
  network_type?: string;
  firmware_version?: string;
  last_seen_at?: string | Date | null;
  __lastRestartDisplay?: string | null;
  last_restart_reason?: string | null;
  last_restart_time?: string | number | Date | null;
}

type DevicesStats = {
  online?: number;
  offline?: number;
  total?: number;
  license_stats?: { active?: number; inactive?: number; expired?: number };
};

type DevicesListResponse = {
  code: number;
  data: { total: number; items: Device[]; stats?: DevicesStats };
  message?: string;
};

const STORAGE_KEY = "device_mgmt_filters_v1";

export default /*@__PURE__*/_defineComponent({
  __name: 'DeviceManagement',
  setup(__props) {

const store = useStore();
const router = useRouter();
let unsubscribe: null | (() => void) = null;
let unsubscribeStatus: null | (() => void) = null;
// 中文注释：会话级持久化键，用于保存筛选与分页参数
const hasPermission = (perm: string) => store.getters.hasPermission(perm);
// 检查是否为超级管理员
const isSuperAdmin = computed(() => {
  const userRole = store.state.user?.role;
  return userRole === "super_admin";
});

// 响应式状态
const loading = ref(false);
const devices = ref<Device[]>([]);
const searchForm = reactive({
  device_id: "",
  deviceName: "",
  status: "",
  license_status: "", // 新增：License状态筛选
  imsi: "", // 新增：IMSI筛选
});
const pagination = reactive({
  page: 1,
  pageSize: 10,
  total: 0,
});

// 新增/编辑弹窗的状态
const dialogVisible = ref(false);
const dialogTitle = ref("");
const isEditMode = ref(false);
const deviceFormRef = ref<FormInstance>();
const deviceForm = reactive({
  id: null,
  device_id: "",
  device_name: "",
  device_model: "",
  hardware_version: "",
  firmware_version: "",
  status: "offline",
  license_status: "inactive",
  license_key: "",
});
const deviceFormRules = reactive<FormRules>({
  device_id: [{ required: true, message: "设备编号不能为空", trigger: "blur" }],
  device_name: [
    { required: true, message: "设备名称不能为空", trigger: "blur" },
  ],
});

// 为统计数据创建响应式状态
const deviceStats = reactive({
  online: 0,
  offline: 0,
  total: 0,
  license_stats: {
    active: 0,
    inactive: 0,
    expired: 0,
  },
});

// 获取设备列表
const fetchDevices = async () => {
  loading.value = true;
  try {
    const params = {
      page: pagination.page,
      page_size: pagination.pageSize,
      device_id: searchForm.device_id || undefined,
      device_name: searchForm.deviceName || undefined,
      status: searchForm.status || undefined,
      license_status: searchForm.license_status || undefined, // 新增：License状态筛选
      imsi: searchForm.imsi || undefined, // 新增：IMSI筛选
    };

    // 使用数据服务层
    const response = await DataService.getDevices(params);
    const raw = (response as { data?: unknown })?.data ?? response;
    const responseData = raw as DevicesListResponse;

    if (responseData.code === 0) {
      devices.value = responseData.data.items;
      pagination.total = responseData.data.total;
      // 更新统计数据
      if (responseData.data.stats) {
        Object.assign(deviceStats, responseData.data.stats);
      }
      // 处理设备列表中的重启原因信息（直接从API响应中获取，无需额外请求）
      (devices.value || []).forEach((d: Device) => {
        if (d.last_restart_reason && d.last_restart_time) {
          // 直接使用formatDateTime处理，不要调用toISOString()，因为后端返回的已经是本地时间
          d.__lastRestartDisplay = `${d.last_restart_reason} @ ${formatDateTime(
            String(d.last_restart_time)
          )}`;
        } else if (d.last_restart_reason) {
          d.__lastRestartDisplay = d.last_restart_reason;
        } else {
          d.__lastRestartDisplay = null;
        }
      });

      // 自动获取设备名称
      await fetchDeviceNames();
    } else {
      ElMessage.error(responseData.message || "获取设备列表失败");
    }
  } catch (error) {
    logger.error("获取设备列表时出错:", error);
    ElMessage.error("网络错误，无法加载设备列表");
  } finally {
    loading.value = false;
  }
};

// 中文注释：持久化当前筛选与分页到会话存储
const persistToStorage = () => {
  try {
    const payload = {
      device_id: searchForm.device_id,
      deviceName: searchForm.deviceName,
      status: searchForm.status,
      license_status: searchForm.license_status,
      page: pagination.page,
      pageSize: pagination.pageSize,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    // 持久化失败时静默忽略，避免打断正常流程
  }
};

// 中文注释：从会话存储恢复筛选与分页
const restoreFromStorage = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw || "{}") || {};
    if (typeof data.device_id === "string")
      searchForm.device_id = data.device_id;
    if (typeof data.deviceName === "string")
      searchForm.deviceName = data.deviceName;
    if (typeof data.status === "string") searchForm.status = data.status;
    if (typeof data.license_status === "string")
      searchForm.license_status = data.license_status;
    if (typeof data.page === "number" && data.page > 0)
      pagination.page = data.page;
    if (typeof data.pageSize === "number" && data.pageSize > 0)
      pagination.pageSize = data.pageSize;
    return true;
  } catch {
    return false;
  }
};

// 搜索处理
const handleSearch = () => {
  pagination.page = 1;
  persistToStorage();
  fetchDevices();
};

// 重置搜索
const resetSearch = () => {
  searchForm.device_id = "";
  searchForm.deviceName = "";
  searchForm.status = "";
  searchForm.license_status = ""; // 新增：重置License状态筛选
  searchForm.imsi = ""; // 新增：重置IMSI筛选
  pagination.page = 1;
  persistToStorage();
  fetchDevices();
};

// 分页大小变化
const handleSizeChange = (size: number) => {
  pagination.pageSize = size;
  persistToStorage();
  fetchDevices();
};

// 当前页码变化
const handleCurrentChange = (page: number) => {
  pagination.page = page;
  persistToStorage();
  fetchDevices();
};

// 处理数据源变化
const handleDataSourceChange = () => {
  logger.info("🔄 检测到数据源变化，重新加载设备列表");
  fetchDevices();
};

// 生命周期钩子
onMounted(() => {
  // 从 URL 查询参数获取 imsi
  const imsi = router.currentRoute.value.query.imsi as string;
  if (imsi) {
    searchForm.device_id = ""; // 清除其他筛选
    searchForm.deviceName = "";
    searchForm.status = "";
    searchForm.license_status = "";
    searchForm.imsi = imsi;
  }

  // 中文注释：从会话存储恢复筛选与分页（若有）
  if (!imsi) {
    restoreFromStorage();
  }
  fetchDevices();

  // 监听数据源变化事件
  window.addEventListener("dataSourceChanged", handleDataSourceChange);

  const websocketManager = import("../services/websocketManager").then(
    (m) => m.default
  );
  websocketManager
    .then((wsManager) => {
      wsManager.autoConnect();

      unsubscribe = wsManager.onMessage((msg: any) => {
        if (typeof msg === "object" && msg !== null) {
          if (msg.type === "notify" && msg.action === "end_call") {
            const eventId = msg.data?.event_id;
            const deviceId = msg.data?.device_id;
            if (
              (currEventId.value && currEventId.value === eventId) ||
              (currDeviceId.value && currDeviceId.value === deviceId)
            ) {
              closeMessageBox();
            }
          } else if (msg.type === "response" && msg.action === "end_call") {
            const eventId = msg.data?.event_id;
            const deviceId = msg.data?.device_id;
            if (
              (currEventId.value && currEventId.value === eventId) ||
              (currDeviceId.value && currDeviceId.value === deviceId)
            ) {
              closeMessageBox();
            }
          }
        }
      });

      unsubscribeStatus = wsManager.onStatusChange(() => undefined);
    })
    .catch(() => undefined);
});

onUnmounted(() => {
  window.removeEventListener("dataSourceChanged", handleDataSourceChange);
  if (typeof unsubscribe === "function") {
    try {
      unsubscribe();
    } finally {
      unsubscribe = null;
    }
  }
  if (typeof unsubscribeStatus === "function") {
    try {
      unsubscribeStatus();
    } finally {
      unsubscribeStatus = null;
    }
  }
});

// 状态显示转换
const getStatusType = (
  status: string
): "success" | "warning" | "info" | "danger" => {
  const statusMap: Record<string, "success" | "warning" | "info" | "danger"> = {
    online: "success",
    offline: "warning",
  };
  return statusMap[status] || "info";
};

const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    online: "在线",
    offline: "离线",
  };
  return textMap[status] || "未知";
};

// 网络类型判定与提示
const normalizeNetType = (row: Device) =>
  String(row?.network_type || "").toLowerCase();
const isWifi = (row: Device) => normalizeNetType(row) === "wifi";
const isCellular = (row: Device) => {
  const v = normalizeNetType(row);
  return v === "4g" || v === "lte" || v === "mobile" || v === "cellular";
};
const getNetworkTypeTooltip = (row: Device) =>
  isWifi(row) ? "WiFi" : isCellular(row) ? "4G" : "";

// License状态显示转换
const getLicenseStatusType = (
  status: string
): "success" | "warning" | "info" | "danger" => {
  const statusMap: Record<string, "success" | "warning" | "info" | "danger"> = {
    active: "success",
    inactive: "warning",
    expired: "danger",
  };
  return statusMap[status] || "info";
};

const getLicenseStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    active: "已激活",
    inactive: "未激活",
    expired: "已过期",
  };
  return textMap[status] || "未知";
};

// 打开新增设备弹窗
const addDevice = () => {
  isEditMode.value = false;
  dialogTitle.value = "注册新设备";
  dialogVisible.value = true;
};

// 打开编辑设备弹窗
const editDevice = (device: Device) => {
  isEditMode.value = true;
  dialogTitle.value = "编辑设备";
  // 使用 nextTick 确保表单在数据赋值前已存在
  nextTick(() => {
    Object.assign(deviceForm, device);
  });
  dialogVisible.value = true;
};

// 关闭弹窗并重置表单
const closeDialog = () => {
  dialogVisible.value = false;
  deviceFormRef.value?.resetFields();
  // 重置状态
  Object.assign(deviceForm, {
    id: null,
    device_id: "",
    device_name: "",
    device_model: "",
    hardware_version: "",
    firmware_version: "",
    status: "offline",
    license_status: "inactive",
  });
};

// 保存设备 (新增或更新)
const handleSave = async () => {
  if (!deviceFormRef.value) return;
  await deviceFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true;
      try {
        let response;
        if (isEditMode.value) {
          // 编辑模式 - PUT 请求
          const url = `${config.apiUrls.DEVICES}/${deviceForm.device_id}`;
          response = await apiClient.put(url, deviceForm);
        } else {
          // 新增模式 - POST 请求 (注册)
          // 根据文档，注册接口是 /api/v1/devices/register
          const url = `${config.apiUrls.DEVICES}/register`;
          response = await apiClient.post(url, deviceForm);
        }

        if (response.data.code === 0) {
          ElMessage.success(isEditMode.value ? "设备更新成功" : "设备注册成功");
          closeDialog();
          fetchDevices();
        } else {
          ElMessage.error(response.data.message || "操作失败");
        }
      } catch (error: unknown) {
        logger.error("保存设备时出错:", error);
        const errMsg =
          (error as { response?: { data?: { message?: string } } }).response
            ?.data?.message || "网络错误，操作失败";
        ElMessage.error(errMsg);
      } finally {
        loading.value = false;
      }
    }
  });
};

// 查看设备详情 (跳转到详情页)
const viewDevice = (device: Device) => {
  router.push(`/device/${device.device_id}`);
};

// 查看通话明细 (跳转到通话明细页)
const viewCallRecords = (device: Device) => {
  router.push(`/call-records/${device.device_id}`);
};

// 自动获取设备名称列表（静默执行）
const fetchDeviceNames = async () => {
  try {
    // 准备请求参数，包含签名验证所需的参数
    const params = {
      pageNum: 1,
      pageSize: 100,
      timestamp: Math.floor(Date.now() / 1000).toString(),
    };

    logger.info("📋 自动获取设备名称列表", params);
    const response = await DataService.getDeviceNames(params);

    if (response.data?.code === 200) {
      const deviceNames = response.data.data?.deviceNames || [];
      logger.info(`成功获取 ${deviceNames.length} 个设备名称`);

      // 静默处理，不显示弹窗
      if (deviceNames.length > 0) {
        // 可以在这里处理设备名称数据，比如更新设备列表中的名称
        logger.info("设备名称数据:", deviceNames.slice(0, 3)); // 只记录前3个
      }
    } else {
      logger.warn("获取设备名称失败:", response.data?.message);
    }
  } catch (error) {
    logger.error("获取设备名称失败:", error);
    // 静默失败，不影响主流程
  }
};

// 使用通用通话弹窗逻辑
const {
  currDeviceId,
  currEventId,
  handleCallResponse,
  startWebRTC,
  leaveRoom,
  updateMessageBoxToAnswering,
  closeMessageBox,
  alertBroadcast,
} = useCallDialog();

// 发起呼叫
const initiateCall = async (device: Device) => {
  logger.debug("[initiateCall] device:", device);
  try {
    // 检查WebSocket连接状态
    const websocketManager = await import("../services/websocketManager").then(
      (m) => m.default
    );

    // 确保WebSocket已连接
    if (websocketManager.getStatus() !== "Connected") {
      logger.warn("WebSocket未连接，尝试自动连接...");
      websocketManager.autoConnect();

      // 等待连接建立
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("WebSocket连接超时"));
        }, 5000);

        const unsubscribe = websocketManager.onStatusChange((status) => {
          if (status === "Connected") {
            clearTimeout(timeout);
            unsubscribe();
            resolve(true);
          } else if (status === "Disconnected") {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error("WebSocket连接失败"));
          }
        });
      });
    }

    // 构建呼叫消息
    const callMessage = {
      type: "command",
      action: "start_call",
      messageId: `msg_${Date.now()}`,
      timestamp: new Date().toISOString(),
      data: {
        device_id: device.device_id,
        source: "frontend",
      },
    };
    logger.debug("[initiateCall] callMessage:", callMessage);

    // 发送呼叫消息
    websocketManager.send(callMessage);
    ElMessage.success(`正在呼叫设备 ${getDeviceDisplayName(device)}...`);

    // 使用Promise包装消息监听，确保只处理一次（避免使用 responsePromise 命名引起混淆）
    const waitForStartCallResponse = new Promise((resolve, reject) => {
      let isHandled = false;
      let timeoutId: number | undefined = undefined;

      const unsubscribe = websocketManager.onMessage((message) => {
        // 防止重复处理
        if (isHandled) {
          return;
        }

        logger.debug("[initiateCall] received message:", message);
        logger.info(
          `收到WebSocket消息: 类型=${message?.type}, 动作=${message?.action}`
        );

        if (message.type === "response" && message.action === "start_call") {
          isHandled = true;
          unsubscribe(); // 立即取消订阅

          // 清理超时定时器
          if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
          }

          if (message.code === 0) {
            ElMessage.success("呼叫成功，正在建立连接...");
            // 直接处理通话，不跳转页面 - Web端发起的呼叫
            handleCallResponse(message, device, true);
            resolve(true);
          } else {
            ElMessage.error(`呼叫失败: ${message.message || "未知错误"}`);
            reject(new Error(message.message || "呼叫失败"));
          }
        } else if (message.type === "notify" && message.action === "end_call") {
          // 处理通话结束通知
          logger.info("收到通话结束通知:", message.data);
          logger.info(
            `当前通话状态: currDeviceId=${currDeviceId.value}, currEventId=${currEventId.value}`
          );

          // 检查是否是当前设备的通话
          if (
            currDeviceId.value === message.data.device_id &&
            currEventId.value === message.data.event_id
          ) {
            logger.info("收到当前设备的通话结束通知，关闭弹窗");
            closeMessageBox();
            ElMessage.info(`设备已结束通话`);
          } else {
            logger.info("不是当前设备的通话，忽略此通知");
          }
        }
      });

      // 设置超时
      timeoutId = window.setTimeout(() => {
        if (!isHandled) {
          isHandled = true;
          unsubscribe();
          reject(new Error("呼叫超时，请检查设备状态"));
        }
      }, 10000); // 10秒超时
    });

    // 等待响应
    await waitForStartCallResponse;
  } catch (error) {
    logger.error("发起呼叫时出错:", error);
    const errorMessage = error instanceof Error ? error.message : "请稍后重试";
    ElMessage.error(`发起呼叫失败: ${errorMessage}`);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_space = _resolveComponent("el-space")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[29] || (_cache[29] = _createElementVNode("audio", {
      id: "deviceManagementAlertSound",
      src: "/sound/alarm.mp3",
      preload: "auto",
      style: {"display":"none"}
    }, null, -1)),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_el_row, { gutter: 20 }, {
        default: _withCtx(() => [
          _createVNode(_component_el_col, { span: 6 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_input, {
                modelValue: searchForm.device_id,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchForm.device_id) = $event)),
                placeholder: "设备编号",
                clearable: ""
              }, {
                prefix: _withCtx(() => [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Search))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 6 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_input, {
                modelValue: searchForm.deviceName,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchForm.deviceName) = $event)),
                placeholder: "设备名称",
                clearable: ""
              }, {
                prefix: _withCtx(() => [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Search))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 4 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_select, {
                modelValue: searchForm.status,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchForm.status) = $event)),
                placeholder: "设备连接状态",
                clearable: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_option, {
                    label: "全部",
                    value: ""
                  }),
                  _createVNode(_component_el_option, {
                    label: "在线",
                    value: "online"
                  }),
                  _createVNode(_component_el_option, {
                    label: "离线",
                    value: "offline"
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          (isSuperAdmin.value)
            ? (_openBlock(), _createBlock(_component_el_col, {
                key: 0,
                span: 4
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_select, {
                    modelValue: searchForm.license_status,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((searchForm.license_status) = $event)),
                    placeholder: "激活状态",
                    clearable: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_option, {
                        label: "全部",
                        value: ""
                      }),
                      _createVNode(_component_el_option, {
                        label: "已激活",
                        value: "active"
                      }),
                      _createVNode(_component_el_option, {
                        label: "未激活",
                        value: "inactive"
                      }),
                      _createVNode(_component_el_option, {
                        label: "已过期",
                        value: "expired"
                      })
                    ]),
                    _: 1
                  }, 8, ["modelValue"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_el_col, { span: 4 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_input, {
                modelValue: searchForm.imsi,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((searchForm.imsi) = $event)),
                placeholder: "IMSI",
                clearable: ""
              }, {
                prefix: _withCtx(() => [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Search))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 4 }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                (hasPermission('device.manage'))
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 0,
                      type: "primary",
                      onClick: handleSearch
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_icon, null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Search))
                          ]),
                          _: 1
                        }),
                        _cache[16] || (_cache[16] = _createTextVNode(" 搜索 ", -1))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (hasPermission('device.manage'))
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 1,
                      onClick: resetSearch
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_icon, null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Refresh))
                          ]),
                          _: 1
                        }),
                        _cache[17] || (_cache[17] = _createTextVNode(" 重置 ", -1))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_component_el_card, { class: "list-card" }, {
          header: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _cache[19] || (_cache[19] = _createElementVNode("span", null, "设备列表", -1)),
              _createElementVNode("div", _hoisted_7, [
                (hasPermission('device.create'))
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 0,
                      type: "primary",
                      onClick: addDevice
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_icon, null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Plus))
                          ]),
                          _: 1
                        }),
                        _cache[18] || (_cache[18] = _createTextVNode(" 注册新设备 ", -1))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ])
          ]),
          default: _withCtx(() => [
            _withDirectives((_openBlock(), _createBlock(_component_el_table, {
              data: devices.value,
              style: {"width":"100%"},
              "table-layout": "auto"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_table_column, {
                  type: "selection",
                  width: "55"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "device_id",
                  label: "设备编号",
                  width: "150"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "device_name",
                  label: "设备名称",
                  "min-width": "180"
                }),
                _createVNode(_component_el_table_column, {
                  prop: "status",
                  label: "连接状态",
                  width: "140"
                }, {
                  default: _withCtx((scope) => [
                    _createVNode(_component_el_space, { alignment: "center" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_tag, {
                          type: getStatusType(scope.row.status)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(getStatusText(scope.row.status)), 1)
                          ]),
                          _: 2
                        }, 1032, ["type"]),
                        (scope.row.status === 'online')
                          ? (_openBlock(), _createBlock(_component_el_tooltip, {
                              key: 0,
                              content: getNetworkTypeTooltip(scope.row),
                              placement: "top"
                            }, {
                              default: _withCtx(() => [
                                (isWifi(scope.row))
                                  ? (_openBlock(), _createElementBlock("img", {
                                      key: 0,
                                      src: _unref(wifiIcon),
                                      class: "net-icon",
                                      alt: "WiFi"
                                    }, null, 8, _hoisted_8))
                                  : (isCellular(scope.row))
                                    ? (_openBlock(), _createElementBlock("img", {
                                        key: 1,
                                        src: _unref(cellularIcon),
                                        class: "net-icon",
                                        alt: "4G"
                                      }, null, 8, _hoisted_9))
                                    : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1032, ["content"]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 1
                }),
                (isSuperAdmin.value)
                  ? (_openBlock(), _createBlock(_component_el_table_column, {
                      key: 0,
                      prop: "license_status",
                      label: "License状态",
                      width: "120"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_tag, {
                          type: getLicenseStatusType(scope.row.license_status)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(getLicenseStatusText(scope.row.license_status)), 1)
                          ]),
                          _: 2
                        }, 1032, ["type"])
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_el_table_column, {
                  prop: "last_seen_at",
                  label: "最后在线时间",
                  "min-width": "160"
                }, {
                  default: _withCtx((scope) => [
                    _createTextVNode(_toDisplayString(_unref(formatDateTime)(scope.row.last_seen_at)), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_table_column, {
                  prop: "firmware_version",
                  label: "固件版本",
                  width: "120"
                }, {
                  default: _withCtx((scope) => [
                    _createVNode(_component_el_tag, {
                      type: scope.row.firmware_version ? 'info' : 'warning',
                      size: "small"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(scope.row.firmware_version || "未知"), 1)
                      ]),
                      _: 2
                    }, 1032, ["type"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_table_column, {
                  label: "操作",
                  width: "260",
                  fixed: "right",
                  align: "center"
                }, {
                  default: _withCtx((scope) => [
                    _createElementVNode("div", _hoisted_10, [
                      _createVNode(_component_el_button, {
                        type: "primary",
                        size: "small",
                        onClick: ($event: any) => (viewDevice(scope.row))
                      }, {
                        default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                          _createTextVNode(" 详情 ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"]),
                      _createVNode(_component_el_button, {
                        type: "success",
                        size: "small",
                        onClick: ($event: any) => (viewCallRecords(scope.row))
                      }, {
                        default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                          _createTextVNode(" 通话明细 ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"]),
                      (hasPermission('device.call'))
                        ? (_openBlock(), _createBlock(_component_el_button, {
                            key: 0,
                            type: "info",
                            size: "small",
                            onClick: ($event: any) => (initiateCall(scope.row)),
                            disabled: scope.row.status !== 'online'
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_el_icon, null, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Phone))
                                ]),
                                _: 1
                              }),
                              _cache[22] || (_cache[22] = _createTextVNode(" 呼叫 ", -1))
                            ]),
                            _: 1
                          }, 8, ["onClick", "disabled"]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_table_column, {
                  prop: "last_restart",
                  label: "最近重启",
                  "min-width": "180"
                }, {
                  default: _withCtx((scope) => [
                    (scope.row.__lastRestartDisplay)
                      ? (_openBlock(), _createBlock(_component_el_tooltip, {
                          key: 0,
                          content: scope.row.__lastRestartDisplay,
                          placement: "top"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", null, _toDisplayString(scope.row.__lastRestartDisplay), 1)
                          ]),
                          _: 2
                        }, 1032, ["content"]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_11, "—"))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["data"])), [
              [_directive_loading, loading.value]
            ]),
            _createElementVNode("div", _hoisted_12, [
              _createVNode(_component_el_pagination, {
                "current-page": pagination.page,
                "onUpdate:currentPage": _cache[5] || (_cache[5] = ($event: any) => ((pagination.page) = $event)),
                "page-size": pagination.pageSize,
                "onUpdate:pageSize": _cache[6] || (_cache[6] = ($event: any) => ((pagination.pageSize) = $event)),
                "page-sizes": [10, 20, 50, 100],
                total: pagination.total,
                layout: "total, sizes, prev, pager, next, jumper",
                onSizeChange: handleSizeChange,
                onCurrentChange: handleCurrentChange
              }, null, 8, ["current-page", "page-size", "total"])
            ])
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_13, [
        _createVNode(_component_el_card, {
          class: "stats-card",
          header: "设备状态统计"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("div", _hoisted_16, [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Monitor))
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("div", _hoisted_18, _toDisplayString(deviceStats.online), 1),
                  _cache[23] || (_cache[23] = _createElementVNode("div", { class: "stat-label" }, "在线", -1))
                ])
              ]),
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("div", _hoisted_20, [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Monitor))
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("div", _hoisted_22, _toDisplayString(deviceStats.offline), 1),
                  _cache[24] || (_cache[24] = _createElementVNode("div", { class: "stat-label" }, "离线", -1))
                ])
              ]),
              _createElementVNode("div", _hoisted_23, [
                _createElementVNode("div", _hoisted_24, [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Warning))
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_25, [
                  _createElementVNode("div", _hoisted_26, _toDisplayString(deviceStats.license_stats?.expired || 0), 1),
                  _cache[25] || (_cache[25] = _createElementVNode("div", { class: "stat-label" }, "License过期", -1))
                ])
              ]),
              _createElementVNode("div", _hoisted_27, [
                _createElementVNode("div", _hoisted_28, [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(DataBoard))
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_29, [
                  _createElementVNode("div", _hoisted_30, _toDisplayString(deviceStats.total), 1),
                  _cache[26] || (_cache[26] = _createElementVNode("div", { class: "stat-label" }, "设备总数", -1))
                ])
              ])
            ])
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((dialogVisible).value = $event)),
      title: dialogTitle.value,
      width: "500px",
      onClose: closeDialog
    }, {
      footer: _withCtx(() => [
        _createElementVNode("span", _hoisted_31, [
          _createVNode(_component_el_button, { onClick: closeDialog }, {
            default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
              _createTextVNode("取消", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: handleSave
          }, {
            default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
              _createTextVNode("保存", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          model: deviceForm,
          rules: deviceFormRules,
          ref_key: "deviceFormRef",
          ref: deviceFormRef,
          "label-width": "100px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "设备编号",
              prop: "device_id"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.device_id,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((deviceForm.device_id) = $event)),
                  disabled: isEditMode.value,
                  placeholder: "例如：f1-a0-11-2d-5c-66"
                }, null, 8, ["modelValue", "disabled"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "设备名称",
              prop: "device_name"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.device_name,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((deviceForm.device_name) = $event)),
                  placeholder: "例如：一楼大厅紧急呼叫器"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "设备型号",
              prop: "device_model"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.device_model,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((deviceForm.device_model) = $event)),
                  placeholder: "例如：EBA-2024"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "硬件版本",
              prop: "hardware_version"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.hardware_version,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((deviceForm.hardware_version) = $event)),
                  placeholder: "例如：1.0.2"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "固件版本",
              prop: "firmware_version"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: deviceForm.firmware_version,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((deviceForm.firmware_version) = $event)),
                  placeholder: "例如：2.1.5"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            (isEditMode.value)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 0,
                  label: "连接状态",
                  prop: "status"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: deviceForm.status,
                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((deviceForm.status) = $event)),
                      placeholder: "设备连接状态"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "在线",
                          value: "online"
                        }),
                        _createVNode(_component_el_option, {
                          label: "离线",
                          value: "offline"
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isEditMode.value)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 1,
                  label: "License状态",
                  prop: "license_status"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_select, {
                      modelValue: deviceForm.license_status,
                      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((deviceForm.license_status) = $event)),
                      placeholder: "License状态"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_option, {
                          label: "已激活",
                          value: "active"
                        }),
                        _createVNode(_component_el_option, {
                          label: "未激活",
                          value: "inactive"
                        }),
                        _createVNode(_component_el_option, {
                          label: "已过期",
                          value: "expired"
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isEditMode.value)
              ? (_openBlock(), _createBlock(_component_el_form_item, {
                  key: 2,
                  label: "License密钥",
                  prop: "license_key"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_input, {
                      modelValue: deviceForm.license_key,
                      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((deviceForm.license_key) = $event)),
                      placeholder: "请输入License密钥"
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["model", "rules"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})